/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import googleMaps from '../util/google-maps.js';
import _ from "underscore";
import util from '../util/util.js';
import moment from "dayjs";
import SortableTableViewModel from "./shared/sortable-table.js";
import mapStyles from "../map/styles";
import markerLayer from "../map/marker-layer.js";
import areasMap from '../map/significant-areas.js';
import regionsMap from '../map/regions.js';
import DrawPolygonTool from "../util/draw-polygon-tool.js";
import h from "../util/dom-builder.js";

import "./survey-summary.js";

import '../util/util.js';
import '../util/knockout-pikaday.js';
import "./shared/popup-menu.js";

var MyDataViewModel = function(birdata) {
	birdata.loadSpeciesSprite();
	document.body.classList.add("birdata-fullscreen");

	var self = this;

	self.iconSprite = require('open-iconic/sprite/open-iconic.min.svg');

	function setupHistory() {
		window.addEventListener('hashchange', handleHashChange);
		handleHashChange();
	}

	function handleHashChange() {
		var surveyID = util.hashParams().surveyID;
		if(surveyID) {
			birdata.survey(surveyID, { _u: true }).then(self.selectedSurvey);
		} else {
			self.selectedSurvey(null);
		}
	}

	self.months = _.map(util.monthsShort(), function(m, i) {
		return {
			month: m,
			index: i + 1
		};
	});

	var drawPolygonTool = new DrawPolygonTool();

	self.loaded = ko.observable(false);

	self.map = ko.observable();
	self.mapOptions = ko.observable();

	self.mapBounds = ko.observable({});
	self.visibleOnMapFilter = ko.observable(false).extend({ rateLimit: 100 });
	self.beforeDateFilter = ko.observable(null);
	self.afterDateFilter = ko.observable(null);
	self.surveyTextFilter = ko.observable(null).extend({rateLimit: {timeout: 500, method: "notifyWhenChangesStop"}});
	self.speciesFilter = ko.observable(null);
	self.monthsFilter = ko.observableArray(_.pluck(self.months, 'index'));
	self.allMonthsFilter = ko.pureComputed({
		read: function() {
			return self.monthsFilter().length === self.months.length;
		},
		write: function(value) {
			self.monthsFilter(value ? _.pluck(self.months, 'index') : []);
		}
	});
	self.monthsFilterDelayed = ko.pureComputed(function() {
		return self.monthsFilter();
	}).extend({ rateLimit: { timeout: 1500, method: "notifyWhenChangesStop"}});

	self.surveyTypeFilter = ko.observableArray();
	self.polygonFilter = ko.observable();

	self.regionFilter = ko.observable(null);
	self.programFilter = ko.observable(null);
	self.areaLayerFilter = ko.observable(null);
	self.areaFilter = ko.observable(null);

	self.regions = ko.observable([]);
	self.programs = ko.observable([]);
	self.areaLayers = ko.observable([]);
	self.areas = ko.observable([]);
	self.surveyTypes = ko.observable([]);

	self.mapMarkers = ko.observable([]);

	self.surveyPoints = ko.observable([]);
	self.myStats = ko.observable();
	self.speciesStats = ko.observable([]);
	self.allSpecies = ko.observable();

	birdata.lookup('survey_type').then(self.surveyTypes);

	var speciesListPromise = birdata.speciesList({ supertaxa: 'all' });
	var speciesMapPromise = speciesListPromise.then(function(species) {
		var map = {};
		_.each(species, function(sp) { map[sp.id] = sp; });
		return map;
	});

	Promise.all([
		birdata.surveyPoints().then(self.surveyPoints),
		birdata.myStats().then(self.myStats),
		speciesListPromise.then(self.allSpecies),
		birdata.regions().then(self.regions),
		birdata.areaLayers().then(self.areaLayers),
		birdata.lookup('program').then(self.programs)
	]).then(function() {
		self.loaded(true);
	});

	self.areaLayerFilter.subscribe(function(areaLayer) {
		self.areaFilter(null);
		self.areas([]);

		if(areaLayer) {
			birdata.areas().then(function(areas) {
				var options = _.chain(areas)
					.where({layer_id: areaLayer.id})
					.sortBy('name')
					.value();

				self.areas(options);
			});
		}
	});

	self.speciesMatchFn = function(option, text, search) {
		text = text.toLowerCase().replace("-", " ");
		search = search.toLowerCase().replace("-", " ");
		return text.indexOf(search);
	};

	self.showSortMenu = ko.observable(false);
	self.sortOptions = [{
		label: "Sort by taxonomy",
		key: function(s) { return s.species.sort; }
	}, {
		label: "Sort by common name",
		key: function(s) { return s.species.common_name; }
	}, {
		label: "Sort by scientific name",
		key: function(s) { return s.species.scientific_name; }
	}, {
		label: "Sort by reporting rate",
		key: function(s) { return -s.rawCount; }
	}];
	self.selectedSortOption = ko.observable(self.sortOptions[0]);

	self.sortedSpeciesStats = ko.pureComputed(function() {
		return _.sortBy(self.speciesStats(), self.selectedSortOption().key);
	});

	self.printSpecies = function() {
		var w = window.open();
		w.document.body.innerHTML = require("../../templates/print/species-stats-list.html");
		w.ko = ko;
		ko.applyBindings({
			sortedSpeciesStats: self.sortedSpeciesStats
		}, w.document.body);
		w.print();
	}

	self.canDownloadSpecies = function() {
		// https://github.com/Modernizr/Modernizr/blob/master/feature-detects/a/download.js
		return !window.externalHost && 'download' in document.createElement('a');
		// return false;
	};

	self.downloadSpecies = function() {
		var csv = _.map(self.sortedSpeciesStats(), function(s) {
			return [
				s.species.common_name,
				s.species.scientific_name,
				s.rawCount,
				s.reportingRate,
				s.breeding ? 'true' : ''
			].join(",");
		}).join("\n");

		csv = "Common Name,Scientific Name,Count,Reporting Rate,Includes Breeding Records\n" + csv;

		util.downloadString(csv, 'species.csv', 'text/csv');
	}

	drawPolygonTool.polygon = self.polygonFilter;
	self.map.subscribe(function(map) {
		drawPolygonTool.map = map;
	});
	self.isDrawingPolygon = drawPolygonTool.isDrawingPolygon;
	self.drawPolygon = function() { drawPolygonTool.drawPolygon(); }
	self.cancelPolygon = function() { drawPolygonTool.cancelPolygon(); }

	self.filterParams = ko.computed(function() {
		var params = {};

		if(self.visibleOnMapFilter()) {
			_.extend(params, self.mapBounds());
		}

		if(self.afterDateFilter()) {
			params.from = moment(self.afterDateFilter()).format("YYYY-MM-DD");
		}

		if(self.beforeDateFilter()) {
			params.to = moment(self.beforeDateFilter()).format("YYYY-MM-DD");
		}

		if(self.surveyTextFilter()) {
			params.survey_text = self.surveyTextFilter();
		}

		if(self.speciesFilter()) {
			params.species_id = self.speciesFilter().id;
		}

		if(self.monthsFilterDelayed() && self.monthsFilterDelayed().length < 12) {
			params.months = _.sortBy(self.monthsFilterDelayed(), _.identity).join(",");
		}

		if(self.surveyTypeFilter().length > 0) {
			params.survey_type = _.pluck(self.surveyTypeFilter(), 'code').join(",");
		}

		if(self.polygonFilter()) {
			params.polygon = drawPolygonTool.base64EncodedPolygon;
		}

		if(self.regionFilter()) {
			params.region_id = self.regionFilter().id;
		}

		if(self.programFilter()) {
			params.program = self.programFilter().code;
		}

		if(self.areaFilter()) {
			params.area_id = self.areaFilter().id;
		} else if(self.areaLayerFilter()) {
			params.area_layer_id = self.areaLayerFilter().id;
		}

		return params;
	});

	self.filtersActive = ko.pureComputed(function() {
		return !_.isEmpty(self.filterParams());
	});

	function surveyTypeString(code) {
		var type = _.findWhere(self.surveyTypes(), { code: code });
		return type ? type.name : code;
	}

	var sources = ko.observable();
	birdata.lookup('source').then(sources);
	function sourceString(code) {
		if(sources()) {
			var s = _.findWhere(sources(), { code: code });
			return s ? s.name : code;
		}
		return code;
	}

	var programs = ko.observable();
	birdata.lookup('program').then(programs);
	function programString(code) {
		if(programs()) {
			var s = _.findWhere(programs(), { code: code });
			return s ? s.name : code;
		}
		return code;
	}

	self.surveyTable = new SortableTableViewModel([
		{
			name: ko.pureComputed(function() {
				if(self.surveysLoaded()) {
					return '<div>My Surveys <div class="survey-count">(' + util.formatNumber(self.surveys().length) + ')<div></div>';
				} else {
					return 'My Surveys';
				}
			}),
			value: function(survey) { var name = survey.survey_point.name; return name ? name.trim().toLowerCase() : ""; },
			html: function(survey) {
				if(survey.survey_point.name) {
					return util.highlightMatches(survey.survey_point.name, self.surveyTextFilter() || "");
				} else {
					return "(Unnamed)";
				}
			},
			id: 'survey-point'
		}, {
			name: 'Date',
			value: function(survey) { return survey.start_date + survey.start_time; },
			formatted: function(survey) {
				// Much faster than using moment.js
				var y = survey.start_date.substr(0, 4),
					m = survey.start_date.substr(5, 2),
					d = survey.start_date.substr(8, 2);
				return d + "/" + m + "/" + y;
				// return moment(survey.start_date).format("DD/MM/YYYY");
			},
			id: 'start_date'
		}, {
			name: 'Type',
			value: function(survey) { return surveyTypeString(survey.survey_type); },
			formatted: function(survey) { return this.value(survey); },
			id: 'survey_type'
		}, {
			name: 'Duration',
			value: function(survey) { return survey.duration_in_minutes; },
			formatted: function(survey) { return util.formatDuration(survey.duration_in_minutes); },
			id: 'duration'
		}, {
			name: 'Program',
			value: function(survey) { return programString(survey.program); },
			formatted: function(survey) { return this.value(survey); },
			id: 'program'
		}, {
			name: 'Source',
			value: function(survey) { return sourceString(survey.source); },
			formatted: function(survey) { return this.value(survey); },
			id: 'source'
		}, {
			name: '',
			id: 'zoom',
			html: function(survey) { return "Map";},
			sortable: false
		}
	], {
		classesForRow: survey => (survey.completed ? 'complete' : 'incomplete')
	});

	function zoomToSurveyPoint(sp) {
		var map = self.map();
		if(map) {
			map.setCenter(new google.maps.LatLng(sp.lat, sp.lon));
			if(map.getZoom() < 16) map.setZoom(16);

			var marker = _.find(self.mapMarkers(), function(marker) {
				return marker.userData.surveyPoint.id == sp.id;
			});
			if(marker) {
				marker.setMap(map);
				google.maps.event.trigger(marker, 'click');
			}
		}
	}

	// Loads surveys in chunks to avoid overloading server
	function loadSurveys(criteria, offset = 0, limit = 500) {
		var params = _.defaults({ offset: offset, limit: limit }, criteria);
		return birdata.mySurveys(params).then(function(surveys) {
			if(surveys.length == limit) {
				return loadSurveys(criteria, offset + limit, limit).then(function(rest) {
					return surveys.concat(rest);
				})
			} else {
				return surveys;
			}
		});
	}

	self.surveyTable.sortDescriptors([{
		column: 'start_date',
		ascending: false
	}]);

	self.surveys = ko.observable([]);
	self.renderedSurveys = ko.observableArray();
	self.surveysLoaded = ko.observable(false);
	var surveyLoadToken;

	// Route polylines
	var routePolylines = [];
	ko.computed(function() {
		var map = self.map();
		if(!map) {
			return;
		}
		var surveys = self.surveys();

		for(let polyline of routePolylines) {
			polyline.setMap(null);
		}

		routePolylines = [];
		let totalCoords = 0;
		for(let survey of self.surveys()) {
			let lines = survey.route_track?.coordinates || [];
			for(let line of lines) {
				totalCoords += line.length;
				if(totalCoords > 100000) {
					console.log("Not showing all routes – coordinate limit exceeded");
					return;
				}
				let polyline = polylineFromGeoJSONCoordinates(line)
				routePolylines.push(polyline);
				polyline.userData = survey;
				google.maps.event.addListener(polyline, 'click', handleRoutePolylineClick);
				polyline.setMap(map);
			}
		}
	});

	function handleRoutePolylineClick(evt) {
		let polyline = this;
		let survey = polyline.userData;
		let map = self.map();
		if(infoWindow) {
			infoWindow.close();
		}

		var surveyDate = util.formatDate(util.parseISODate(survey.start_date));

		let title = "Survey route track";
		infoWindowContent = h("div", { style: "display: flex; flex-direction: column; gap: 0.5em;" }, [
			h("p", survey.survey_point?.name),
			h("p", "Surveyed: " + surveyDate),
			h("p",
				h("button", { "$click": () => showSurvey(survey) }, "View Survey"))
		]);

		infoWindow = new google.maps.InfoWindow({
			headerContent: h("h3", { style: "margin-top: 1.5em" }, title),
			content: infoWindowContent,
			position: evt.latLng
		});

		infoWindow.open(self.map());
	}

	function polylineFromGeoJSONCoordinates(line) {
	    return new google.maps.Polyline({
	      path: line.map(([x,y]) => ({ lat: y, lng: x })),
	      geodesic: true,
	      strokeColor: "#FF0000",
	      strokeOpacity: 1.0,
	      strokeWeight: 2
	    })
	  }

	self.filterParams.subscribe(refresh);
	refresh();

	function refresh() {
		self.surveys([]);
		self.surveysLoaded(false);
		// Fetch surveys based on filter params
		var token = surveyLoadToken = {};
		loadSurveys(self.filterParams()).then(function(surveys) {
			if(token != surveyLoadToken) return; // ignore stale request
			// Update UI with new surveys
			self.surveys(surveys);
		}).finally(function() {
			self.surveysLoaded(true);
		});
	}

	self.sortedSurveys = ko.pureComputed(function() {
		var surveys = [ ...self.surveys() ];

		// Sort surveys according to table sort criteria
		_.each(self.surveyTable.sortDescriptors(), function(descriptor) {
			if(!descriptor.ascending) surveys.reverse();
			var column = self.surveyTable.column(descriptor.column);
			surveys = _.sortBy(surveys, column.params.value);
			if(!descriptor.ascending) surveys.reverse();
		});

		// Always show incomplete surveys on top
		surveys = _.sortBy(surveys, 'completed');

		return surveys;
	});

	self.speciesLoaded = ko.observable(false);
	var speciesLoadToken;

	ko.computed(function() {
		self.speciesStats([]);
		self.speciesLoaded(false);
		var token = speciesLoadToken = {};

		Promise.all([
			birdata.mySurveys(_.defaults({ summarize: 'count' }, self.filterParams())),
			birdata.mySurveys(_.defaults({ summarize: 'surveys_by_species' }, self.filterParams())),
			speciesMapPromise
		]).then(function([countStat, speciesStats, speciesMap]) {
				if(token != speciesLoadToken) return; // ignore stale request

				speciesStats = _.map(speciesStats, function(s) {
					return {
						species: speciesMap[s.species_id],
						count: util.formatNumber(s.count),
						rawCount: s.count,
						breeding: s.breeding_count > 0,
						reportingRate: (s.count * 100 / countStat.count).toFixed(2) + "%"
					}
				});
				speciesStats = _.sortBy(speciesStats, function(s) { return s.species.sort; });

				self.speciesStats(speciesStats);
			}
		).finally(function() {
			self.speciesLoaded(true);
		});
	});

	var surveyPointLayer;

	ko.computed(function() {
		var params = self.filterParams();
		var map = self.map();

		if(!map || !params) return;

		var surveyPointIds = {};
		_.each(self.surveys(), function(survey) {
			surveyPointIds[survey.survey_point.id] = true;
		});

		if(surveyPointLayer) {
			surveyPointLayer.setFilter(function(marker) {
				return surveyPointIds[marker.marker.userData.surveyPoint.id];
			});
		}
	});

	var infoWindow, infoWindowContent;
	function handleMarkerClick(evt) {
		var map = self.map();

		var marker = this;
		var surveyPoint = marker.userData.surveyPoint;
		if(infoWindow) {
			infoWindow.close();
		}

		var surveys = _.filter(self.surveys(), function(survey) {
			return survey.survey_point.id == surveyPoint.id;
		});

		var lastSurvey = _.last(_.sortBy(surveys, 'start_date'));
		var lastSurveyDate = lastSurvey ? util.formatDate(util.parseISODate(lastSurvey.start_date)) : null;

		let title = "Survey Point: " + (surveyPoint.name || "(Unnamed)");
		infoWindowContent = h("div", { style: "display: flex; flex-direction: column; gap: 0.5em;" }, [
			h("p", "Surveys recorded here: " + surveys.length),
			lastSurvey ? h("p", "Last surveyed: " + lastSurveyDate) : "",
			h("p",
				h("button", { "class": "zoom button", $click: () => zoomToSurveyPoint(surveyPoint) }, "Zoom to here"))
		]);

		function updateZoomButton() {
			let zoomButton = infoWindowContent.querySelector(".zoom.button");
			if(zoomButton) {
				zoomButton.style.display = map.getZoom() < 16 ? 'block' : 'none';
			}
		}

		// Hide zoom button if user already sufficiently zoomed in
		if(!infoWindow) {
			google.maps.event.addListener(map, 'zoom_changed', function() {
				setTimeout(updateZoomButton);
			});
		}

		infoWindow = new google.maps.InfoWindow({
			headerContent: h("h3", { style: "margin-top: 1.5em" }, title),
			content: infoWindowContent
		});

		updateZoomButton();

		infoWindow.open(self.map(), marker);
	}

	googleMaps.load().then(function() {
		self.mapOptions(googleMaps.defaultOptions({
			center: new google.maps.LatLng(-22.50831, 136.078612),
			zoom: 4
		}));
	});

	ko.computed(function() {
		var map = self.map();
		var surveyPoints = self.surveyPoints();
		var markers = [];

		if(map && surveyPoints && !surveyPointLayer) {
			surveyPointLayer = markerLayer.layer(map, {
				density: 3,
			});

			_.each(surveyPoints, function(p) {
				var marker = new google.maps.Marker({
					position: new google.maps.LatLng(p.lat, p.lon),
					title: p.name,
					icon: mapStyles.surveyIcon(false),
					userData: {
						surveyPoint: p
					}
				});
				google.maps.event.addListener(marker, 'click', handleMarkerClick);
				surveyPointLayer.addMarker(marker);
				markers.push(marker);
			});
			self.mapMarkers(markers);

			// Zoom to fit survey points on map
			if (surveyPoints.length == 1) {
				map.setCenter({ lat: surveyPoints[0].lat, lng: surveyPoints[0].lon });
				map.setZoom(12);
			} else if (surveyPoints.length > 1) {
				var bounds = new google.maps.LatLngBounds();
				_.each(surveyPoints, function(p) {
					bounds.extend({ lat: p.lat, lng: p.lon });
				});
				map.fitBounds(bounds);
			}

			// Region and area map layers

			function hoverContentPromise(poly) {
				switch(poly.userData.layerID) {
					case "areas":
						return Promise.all([
							birdata.area(poly.userData.id),
							areasMap.significanceDescription(poly.userData.id)
						]).then(([area, description]) => {
							if(area) {
								return area.name + (description ? "<hr>" + description : "");
							} else {
								return null;
							}
						});
				}
			}

			var hoverOverlay = googleMaps.hoverOverlay(map);
			var polygonEvents = {
				mousemove: function(evt) {
					hoverOverlay.position(evt.latLng);
				},
				mouseover: function(evt) {
					var featureID = hoverOverlay.featureID = this.userData.id;
					var layerID = hoverOverlay.layerID = this.userData.layerID;
					hoverOverlay.position(evt.latLng);

					hoverContentPromise(this).then(function(content) {
						if(hoverOverlay.featureID != featureID || hoverOverlay.layerID != layerID) return; // ignore stale

						hoverOverlay.html(content);
						hoverOverlay.show();
					});
				},
				mouseout: function(evt) {
					hoverOverlay.featureID = hoverOverlay.layerID = null;
					hoverOverlay.hide();
				},
			};

			var areaMapLayer = areasMap.showLayer(map, {
				layerID: "areas",
				polygonEvents: polygonEvents
			});

			ko.computed(function() {
				var areaLayer = self.areaLayerFilter();
				var area = self.areaFilter();
				areaMapLayer.setFeatureFilter(function(feature) {
					var attrs = feature.userData && feature.userData.attributes;
					if(!attrs) return true; //Needed?
					if(!areaLayer) return false;
					if(!area) return areaLayer.id == attrs.layer_id;
					else return area.id == attrs.id;
				});
			});

			var regionMapLayer = regionsMap.showLayer(map, {
				layerID: "regions"
			});

			ko.computed(function() {
				var region = self.regionFilter();
				regionMapLayer.setFeatureFilter(function(feature) {
					var attrs = feature.userData && feature.userData.attributes;
					if(!attrs) return true; //Needed?
					if(!region) return false;
					return region.id == attrs.id;
				});
			});

			// -----
		}
	});

	ko.computed(function() {
		var map = self.map();
		if(map) {
			google.maps.event.addListener(map, 'idle', function() {
				var bounds = map.getBounds();
				self.mapBounds({
					xmin: bounds.getSouthWest().lng(),
					ymin: bounds.getSouthWest().lat(),
					xmax: bounds.getNorthEast().lng(),
					ymax: bounds.getNorthEast().lat()
				});
			});

			map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(googleMaps.dragZoomControl(map));
			// Add map type selector
			// map.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(googleMaps.mapTypeSelectorControl(map));
		}
	});

	function getSurveyMarker(survey) {
		return _.find(self.mapMarkers(), function(marker) {
			return marker.userData.surveyPoint.id == survey.survey_point.id;
		});
	}

	self.speciesImageURL = function(species) {
		return birdata.speciesImageURL(species.id, 'small_square');
	};

	self.mouseOverSurvey = function(survey) {
		var marker = getSurveyMarker(survey);
		if(marker) {
			marker.setOptions({
				icon: mapStyles.surveyIcon(true)
			});
			marker.setZIndex(10000);
		}
	};

	self.mouseOutSurvey = function(survey) {
		var marker = getSurveyMarker(survey);
		if(marker) {
			marker.setOptions({
				icon: mapStyles.surveyIcon(false)
			});
			marker.setZIndex(1);
		}
	};

	self.clickSurvey = function(survey) {
		showSurvey(survey);
		return true;
	};

	self.clickTable = function(vm, evt) {
		var i = self.surveyTable.eventRowIndex(evt);

		if(i !== null) {
			var survey = self.renderedSurveys()[i];
			var col = self.surveyTable.eventColumnID(evt);

			if(col == 'zoom') {
				zoomToSurveyPoint(survey.survey_point);
			} else {
				self.clickSurvey(survey);
			}
		}
	}

	self.mouseOutTable = function(vm, evt) {
		var i = self.surveyTable.eventRowIndex(evt);
		if(i !== null) {
			var survey = self.renderedSurveys()[i];
			self.mouseOutSurvey(survey);
		}
	}

	self.mouseOverTable = function(vm, evt) {
		var i = self.surveyTable.eventRowIndex(evt);
		if(i !== null) {
			var survey = self.renderedSurveys()[i];
			self.mouseOverSurvey(survey);
		}
	}

	var goBackOnClose = false;

	function showSurvey(survey) {
		goBackOnClose = true;
		util.updateHashParams({ surveyID: survey.id });

		self.selectedSurvey(survey);

		// Load sightings async.
		birdata.survey(survey.id, { _u: true }).then(function(fullSurvey) {
			if(self.selectedSurvey() == survey) {
				self.selectedSurvey(fullSurvey);
			}
		});
	}

	self.closeSurvey = function() {
		if(goBackOnClose) {
			window.history.back();
		} else {
			util.updateHashParams({}, ['surveyID']);
			self.selectedSurvey(null);
		}
	};

	self.selectedSurvey = ko.observable(null);

	self.afterDelete = function() {
		self.closeSurvey();
		// Refresh
		refresh();
	}

	self.showExportDialog = ko.observable(false);


	self.downloadSurveys = function(format) {
		birdata.mySurveysExportURL(self.filterParams(), format).then(function(url) {
			document.location.href = url;
		});
	};

	setupHistory();
};

ko.components.register('my-data', {
	viewModel: function() {
		return new MyDataViewModel(birdata);
	},
	template: require('../../templates/my-data.html')
});

export default MyDataViewModel;
